import {
  decompose,
  decomposeProperties,
  AtomicValueWithNesting,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { compose } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/composer';
import {
  getParentShorthands,
  isShorthand,
  getShorthandAtomics,
} from '../grouping/propertyRelationships';
import { getEquivalentSiblings } from '../grouping/propertyEquivalents';
import {
  decomposeCSSProperties,
  decomposeCSSPropertiesWithEligibility,
} from './decompositionUtils';
import type { PropertyMetadata, StyleSDKClient } from './types';
import pickBy from 'lodash/pickBy';

/**
 * Complex engine for CSS Properties with shorthand/atomic relationships.
 *
 * Handles:
 * - Shorthand decomposition (border → borderWidth, borderStyle, borderColor)
 * - Atomic composition (borderWidth + borderStyle + borderColor → border)
 * - Sibling propagation (borderWidth → borderLeftWidth, borderRightWidth, etc.)
 * - Multi-level parent relationships (borderLeftColor → borderColor → border)
 */
export class CSSPropertiesEngine {
  private readonly allowedProperties: Set<string>;

  constructor(
    allowedProperties: readonly string[],
    private updateSiblings: boolean,
    private sdkClient: StyleSDKClient,
  ) {
    this.allowedProperties = new Set(allowedProperties);
  }

  /**
   * Discover CSS properties from manifest and Document Model.
   */
  async discoverProperties(): Promise<Record<string, PropertyMetadata>> {
    const definitions = await this.sdkClient.getStyleDefinitions();
    const styles = await this.sdkClient.getStyles();

    const cssProps = definitions.cssProperties || {};
    const rawStyles = styles.cssProperties || {};

    const allMetadata = CSSPropertiesEngine.buildMetadata(cssProps, rawStyles);

    // Filter by allowedProperties
    return pickBy(allMetadata, (meta) =>
      this.allowedProperties.has(meta.cssPropertyType),
    );
  }

  static getManifestMetadata(
    cssProps: Record<string, any>,
  ): Record<string, PropertyMetadata> {
    const metadata: Record<string, PropertyMetadata> = {};

    for (const [key, def] of Object.entries(cssProps)) {
      const cssPropertyType = (def as any).type || key;

      metadata[key] = {
        key,
        isShorthand: isShorthand(key),
        namespace: 'cssProperties',
        cssPropertyType,
      };
    }

    return metadata;
  }

  static getAtomicsMetadata(
    rawStyles: Record<string, string>,
    cssProps: Record<string, any>,
    metadata: Record<string, PropertyMetadata>,
  ): Record<string, PropertyMetadata> {
    const expandedMetadata = { ...metadata };

    for (const key of Object.keys(rawStyles)) {
      if (metadata[key]) {
        continue;
      }

      const parents = getParentShorthands(key);
      const parentShorthand = parents.find((p) => metadata[p]);

      if (parentShorthand) {
        expandedMetadata[key] = {
          key,
          isShorthand: false,
          namespace: 'cssProperties',
          cssPropertyType:
            (cssProps[parentShorthand] as any)?.type || parentShorthand,
          isExpandedAtomic: true,
        };
      }
    }

    return expandedMetadata;
  }

  /**
   * Build PropertyMetadata for CSS properties from manifest definitions and raw styles.
   *
   * 1. Iterates definitions: cssPropertyType = def.type || key, isShorthand via propertyRelationships
   * 2. Iterates rawStyles for expanded atomics: checks getParentShorthands(key),
   *    adds with isExpandedAtomic: true, inherits cssPropertyType from parent
   */
  static buildMetadata(
    cssPropsDefinitions: Record<string, any>,
    rawStyles: Record<string, string>,
  ): Record<string, PropertyMetadata> {
    // Add manifest properties
    const manifestMetadata =
      CSSPropertiesEngine.getManifestMetadata(cssPropsDefinitions);

    // Add expanded atomics from DM
    const metadata: Record<string, PropertyMetadata> =
      CSSPropertiesEngine.getAtomicsMetadata(
        rawStyles,
        cssPropsDefinitions,
        manifestMetadata,
      );

    return metadata;
  }

  /**
   * Get all CSS property values with decomposition.
   * Includes eligibility information - properties defined in metadata but without values
   * will appear in decomposed result with empty arrays.
   */
  async getAllValues(metadata: Record<string, PropertyMetadata>): Promise<{
    raw: Record<string, string>;
    decomposed: Record<string, AtomicValueWithNesting[]>;
  }> {
    const styles = await this.sdkClient.getStyles();
    const cssProps = styles.cssProperties || {};

    const raw = this.filterRawValues(cssProps, metadata);
    // Get definitions from metadata for eligibility tracking
    const definitions = Object.keys(metadata);
    // Use utility function with eligibility information
    const decomposed = decomposeCSSPropertiesWithEligibility(raw, definitions);

    return { raw, decomposed };
  }

  private filterRawValues(
    cssProps: Record<string, string>,
    metadata: Record<string, PropertyMetadata>,
  ): Record<string, string> {
    const raw: Record<string, string> = {};

    for (const key of Object.keys(metadata)) {
      if (cssProps[key] !== undefined) {
        raw[key] = cssProps[key];
      }
    }

    return raw;
  }

  /**
   * Update CSS properties with smart propagation (siblings, parents, atomics).
   * Returns the updates without applying them (coordinator will apply).
   */
  async updateProperties(
    changes: Record<string, string>,
    current: {
      raw: Record<string, string>;
      decomposed: Record<string, AtomicValueWithNesting[]>;
      metadata: Record<string, PropertyMetadata>;
    },
  ): Promise<Record<string, string>> {
    const changedKeys = new Set(Object.keys(changes));
    const affected = this.findAffectedProperties(
      changedKeys,
      changes,
      current.metadata,
    );
    const updates = this.updateAffectedProperties(affected, changes, current);
    // Filter updates using atomic-level change detection
    const meaningful = this.filterMeaningfulUpdates(
      updates.cssProperties || {},
      current,
    );

    return meaningful;
  }

  /**
   * Update CSS properties directly without propagation.
   * Returns the updates without applying them (coordinator will apply).
   */
  async setPropertiesDirectly(
    changes: Record<string, string>,
    current: {
      metadata: Record<string, PropertyMetadata>;
    },
  ): Promise<Record<string, string>> {
    const updates: Record<string, string> = {};

    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) {
        continue;
      }

      if (current.metadata[key]) {
        updates[key] = value;
      }
    }

    return updates;
  }

  private async applyUpdates(updates: {
    cssProperties?: Record<string, string>;
  }): Promise<void> {
    if (
      !updates.cssProperties ||
      Object.keys(updates.cssProperties).length === 0
    ) {
      return;
    }

    await this.sdkClient.setStyles(updates);
  }

  private findAffectedProperties(
    changedKeys: Set<string>,
    changes: Record<string, string>,
    metadata: Record<string, PropertyMetadata>,
  ): Set<string> {
    const affected = new Set(changedKeys);

    // Separate variable changes from regular changes
    const regularKeys = new Set<string>();
    const variableKeys = new Set<string>();

    for (const key of changedKeys) {
      if (this.isCssVariable(changes[key])) {
        variableKeys.add(key);
      } else {
        regularKeys.add(key);
      }
    }

    // Metadata may contain only atomics, while the incoming change key
    // is still a shorthand. Example: `borderTop` must decompose even if metadata
    // only contains `borderTopWidth`, `borderTopStyle`, and `borderTopColor`.
    // The allowed-properties filter below keeps only the atomics in this group.
    for (const key of regularKeys) {
      if (metadata[key]?.isShorthand || isShorthand(key)) {
        const atomics = getShorthandAtomics(key);
        atomics.forEach((atomic) => affected.add(atomic));
      }
    }

    // For ALL changes: add siblings if enabled
    if (this.updateSiblings) {
      this.addSiblings(changedKeys, affected);
    }

    // Filter to only properties that are allowed to be updated based on manifest
    // Always preserve variable keys (they bypass decomposition)
    return this.filterManifestAllowedProperties(
      affected,
      metadata,
      variableKeys,
    );
  }

  /**
   * Filter properties to only those allowed by manifest rules:
   * - For shorthands in manifest: allow their 1-level atomics (not the shorthand itself)
   * - For atomics in manifest: allow updating them directly
   * - Exception: CSS variable changes are always allowed (they bypass decomposition)
   *
   * This ensures we update at the atomic level for regular values,
   * but preserve variables as-is.
   */
  private filterManifestAllowedProperties(
    properties: Set<string>,
    metadata: Record<string, PropertyMetadata>,
    variableKeys: Set<string>,
  ): Set<string> {
    const allowed = new Set<string>();

    // Get properties that are directly in manifest (not expanded atomics)
    const manifestProperties = Object.keys(metadata).filter(
      (key) => !metadata[key].isExpandedAtomic,
    );

    // For each manifest property, determine what atomics we're allowed to update
    for (const manifestProp of manifestProperties) {
      const meta = metadata[manifestProp];

      if (meta.isShorthand) {
        // For shorthands: allow their 1-level atomics
        const atomics = getShorthandAtomics(manifestProp);
        atomics.forEach((atomic) => allowed.add(atomic));
      } else {
        // For atomics: allow updating them directly
        allowed.add(manifestProp);
      }
    }

    // Filter: allow atomics OR (bounded) CSS variable changes.
    //
    // Important: variable updates must NOT allow "unknown" properties to leak through.
    // We only bypass decomposition for variable values if the property is connected to the manifest
    // (either present in metadata or has a parent shorthand present in metadata).
    return new Set(
      Array.from(properties).filter((prop) => {
        // Always allow atomics (regular updates)
        if (allowed.has(prop)) {
          return true;
        }
        // Allow CSS variable changes only when this property belongs to the manifest (directly or via parents).
        return (
          variableKeys.has(prop) && this.isVariableUpdateAllowed(prop, metadata)
        );
      }),
    );
  }

  /**
   * Variable-value updates bypass decomposition, but they still must be scoped to manifest-related properties.
   *
   * Allowed when:
   * - The property exists in metadata (manifest property or expanded atomic)
   * - OR it has at least one parent shorthand present in metadata (e.g. borderTopColor -> borderColor -> border)
   *
   * This prevents leaking updates for completely unknown properties when metadata is empty/partial.
   */
  private isVariableUpdateAllowed(
    property: string,
    metadata: Record<string, PropertyMetadata>,
  ): boolean {
    if (metadata[property]) {
      return true;
    }

    const parents = getParentShorthands(property);
    return parents.some((p) => Boolean(metadata[p]));
  }

  private findParentShorthands(
    changedKeys: Set<string>,
    metadata: Record<string, PropertyMetadata>,
    affected: Set<string>,
  ): void {
    const toProcess = Array.from(changedKeys);

    while (toProcess.length > 0) {
      const key = toProcess.shift()!;
      const parents = getParentShorthands(key);

      for (const parent of parents) {
        if (metadata[parent] && !affected.has(parent)) {
          affected.add(parent);
          toProcess.push(parent);
        }
      }
    }
  }

  private addSiblings(changedKeys: Set<string>, affected: Set<string>): void {
    for (const key of changedKeys) {
      const siblings = this.getSiblings(key);
      siblings.forEach((sibling) => affected.add(sibling));
    }
  }

  private updateAffectedProperties(
    affected: Set<string>,
    changes: Record<string, string>,
    current: {
      raw: Record<string, string>;
      decomposed: Record<string, AtomicValueWithNesting[]>;
      metadata: Record<string, PropertyMetadata>;
    },
  ): { cssProperties: Record<string, string> } {
    const updates: Record<string, string> = {};
    const sorted = this.sortPropertiesByHierarchy(
      Array.from(affected),
      current.metadata,
    );
    for (const key of sorted) {
      let meta = current.metadata[key];

      // Properties without metadata are decomposed atomics (e.g., borderColor when only border is in manifest)
      // For these, use the value from changes if available

      // Or it could be atomic of manifest property but in meta we will have atomices of it, like borderColor when only border is in manifest and borderLeftColor is in meta
      // In this case we need to create a meta for the atomic property only when all of its atomices are in changes
      if (!meta) {
        if (changes[key] !== undefined) {
          updates[key] = changes[key];
          continue;
        }
        const isShorthandAtomic = isShorthand(key);
        if (isShorthandAtomic) {
          const atomics = getShorthandAtomics(key);
          const allAtomicsInMeta = atomics.every(
            (atomic) => changes[atomic] !== undefined,
          );
          if (allAtomicsInMeta) {
            meta = {
              key,
              isShorthand: true,
              namespace: 'cssProperties',
              cssPropertyType: key,
            };
          } else {
            continue;
          }
        } else {
          continue;
        }
      }

      const update = this.computePropertyUpdate(
        key,
        meta,
        changes,
        current,
        updates,
      );

      if (update !== undefined) {
        updates[key] = update;
      }
    }
    return { cssProperties: updates };
  }

  private sortPropertiesByHierarchy(
    properties: string[],
    metadata: Record<string, PropertyMetadata>,
  ): string[] {
    return properties.sort((a, b) => {
      const aIsShorthand = metadata[a]?.isShorthand || false;
      const bIsShorthand = metadata[b]?.isShorthand || false;

      if (!aIsShorthand && bIsShorthand) {
        return -1;
      }
      if (aIsShorthand && !bIsShorthand) {
        return 1;
      }

      if (aIsShorthand && bIsShorthand) {
        const aDepth = this.getShorthandDepth(a);
        const bDepth = this.getShorthandDepth(b);
        return aDepth - bDepth;
      }

      return 0;
    });
  }

  private getShorthandDepth(property: string): number {
    let depth = 0;
    let current = property;
    let parents = getParentShorthands(current);

    while (parents.length > 0) {
      depth++;
      current = parents[0];
      parents = getParentShorthands(current);
    }

    return depth;
  }

  private computePropertyUpdate(
    key: string,
    meta: PropertyMetadata,
    changes: Record<string, string>,
    current: {
      raw: Record<string, string>;
      decomposed: Record<string, AtomicValueWithNesting[]>;
    },
    updates: Record<string, string>,
  ): string | undefined {
    if (meta.isShorthand) {
      return this.computeShorthandUpdate(key, changes, current, updates);
    } else {
      return this.computeAtomicUpdate(key, changes, current);
    }
  }

  private computeShorthandUpdate(
    key: string,
    changes: Record<string, string>,
    current: {
      raw: Record<string, string>;
      decomposed: Record<string, AtomicValueWithNesting[]>;
    },
    updates: Record<string, string>,
  ): string | undefined {
    if (changes[key] !== undefined) {
      return changes[key];
    }

    const atomicValues = this.buildAtomicValues(key, changes, current, updates);
    if (Object.keys(atomicValues).length === 0) {
      return undefined;
    }

    const composed = compose(key, atomicValues);
    return composed !== null ? composed : undefined;
  }

  private buildAtomicValues(
    shorthand: string,
    changes: Record<string, string>,
    current: {
      raw: Record<string, string>;
      decomposed: Record<string, AtomicValueWithNesting[]>;
    },
    updates: Record<string, string>,
  ): Record<string, string> {
    const atomicValues: Record<string, string> = {};
    const atomics = getShorthandAtomics(shorthand);

    for (const atomic of atomics) {
      const value =
        updates[atomic] ||
        changes[atomic] ||
        current.decomposed[atomic]?.[0]?.value ||
        current.raw[atomic];

      if (value !== undefined) {
        atomicValues[atomic] = value;
      }
    }

    return atomicValues;
  }

  private computeAtomicUpdate(
    key: string,
    changes: Record<string, string>,
    current: {
      raw: Record<string, string>;
      decomposed: Record<string, AtomicValueWithNesting[]>;
    },
  ): string | undefined {
    if (changes[key] !== undefined) {
      return changes[key];
    }

    const siblingValue = this.getSiblingValue(key, changes);
    if (siblingValue !== undefined) {
      return siblingValue;
    }

    const fromShorthand = this.getValueFromChangedShorthand(key, changes);
    if (fromShorthand !== undefined) {
      return fromShorthand;
    }

    return current.decomposed[key]?.[0]?.value || current.raw[key];
  }

  private getSiblingValue(
    key: string,
    changes: Record<string, string>,
  ): string | undefined {
    if (!this.updateSiblings) {
      return undefined;
    }

    const siblings = this.getSiblings(key);
    for (const sibling of siblings) {
      if (changes[sibling] !== undefined) {
        return changes[sibling];
      }
    }

    return undefined;
  }

  private getValueFromChangedShorthand(
    atomic: string,
    changes: Record<string, string>,
  ): string | undefined {
    const parents = getParentShorthands(atomic);

    for (const parent of parents) {
      if (changes[parent] !== undefined) {
        const decomposed = decompose(parent, changes[parent]);
        if (decomposed && decomposed[atomic] !== undefined) {
          const value = decomposed[atomic];
          return value !== null ? value : undefined;
        }
      }
    }

    return undefined;
  }

  private getSiblings(property: string): string[] {
    return getEquivalentSiblings(property);
  }

  /**
   * Filters updates to only include meaningful changes at atomic level.
   *
   * Algorithm:
   * 1. Decompose ALL updates to lowest atomics (with source tracking)
   * 2. Compare merged atomics with current decomposed values (with SPX normalization)
   * 3. Keep only atomics that differ
   * 4. Reconstruct original source properties from meaningful atomics
   *
   * This handles cascading changes correctly (e.g., border + borderWidth both updating borderTopWidth).
   *
   * @param updates - Computed updates from updateAffectedProperties
   * @param current - Current raw and decomposed values
   * @returns Filtered updates with only meaningful changes
   *
   * @example
   * Current: { borderTopWidth: '2px', borderRightWidth: '2px', ... }
   * Updates: { border: '2px solid blue', borderWidth: '2px' }
   * Step 1: Both decompose to borderTopWidth: '2px', etc.
   * Step 2: All atomics match current
   * Result: {} (no meaningful change)
   */
  private filterMeaningfulUpdates(
    updates: Record<string, string>,
    current: {
      raw: Record<string, string>;
      decomposed: Record<string, AtomicValueWithNesting[]>;
    },
  ): Record<string, string> {
    // Separate variable updates from regular updates
    const variableUpdates: Record<string, string> = {};
    const regularUpdates: Record<string, string> = {};

    for (const [key, value] of Object.entries(updates)) {
      if (this.isCssVariable(value)) {
        variableUpdates[key] = value;
      } else {
        regularUpdates[key] = value;
      }
    }

    // Step 1: Decompose regular updates to atomics with source tracking and depth
    const updateAtomics: Record<
      string,
      { value: string; sourceProperty: string; depth: number }
    > = {};

    for (const [updateKey, updateValue] of Object.entries(regularUpdates)) {
      const atomicsWithDepth = this.decomposePropertyToAtomicsWithDepth(
        updateKey,
        updateValue,
        0,
      );

      for (const [atomic, { value, depth }] of Object.entries(
        atomicsWithDepth,
      )) {
        const existing = updateAtomics[atomic];

        // Specificity rules:
        // 1. No existing value → Take this one
        // 2. Different values → Most atomic (lowest depth) wins
        // 3. Same values → Most shorthand (highest depth) wins (prefer concise)
        if (!existing) {
          updateAtomics[atomic] = {
            value,
            sourceProperty: updateKey,
            depth,
          };
        } else if (value !== existing.value) {
          // Different values - prefer more atomic (lower depth)
          if (depth < existing.depth) {
            updateAtomics[atomic] = {
              value,
              sourceProperty: updateKey,
              depth,
            };
          }
        } else {
          // Same values - prefer more shorthand (higher depth = more concise)
          if (depth > existing.depth) {
            updateAtomics[atomic] = {
              value,
              sourceProperty: updateKey,
              depth,
            };
          }
        }
      }
    }
    // Step 2: Filter to only atomics that differ from current
    const meaningfulAtomics: typeof updateAtomics = {};

    for (const [atomic, { value, sourceProperty, depth }] of Object.entries(
      updateAtomics,
    )) {
      // Get current value - prefer decomposed (most specific) over raw
      let currentValue = current.decomposed[atomic]?.[0]?.value;
      if (currentValue === undefined) {
        currentValue = current.raw[atomic];
      }

      // Normalize both values (treat spx as px)
      const normalizedUpdate = this.normalizeValue(value);
      const normalizedCurrent = this.normalizeValue(currentValue || '');

      if (normalizedUpdate !== normalizedCurrent) {
        meaningfulAtomics[atomic] = { value, sourceProperty, depth };
      }
    }
    // Step 3: Reconstruct original source properties from meaningful atomics
    const meaningful: Record<string, string> = {};

    // Add regular updates that have meaningful atomic changes
    if (Object.keys(meaningfulAtomics).length > 0) {
      // Group atomics by source property
      const atomicsBySource: Record<string, Set<string>> = {};
      for (const [atomic, { sourceProperty }] of Object.entries(
        meaningfulAtomics,
      )) {
        if (!atomicsBySource[sourceProperty]) {
          atomicsBySource[sourceProperty] = new Set();
        }
        atomicsBySource[sourceProperty].add(atomic);
      }

      // Return source properties that have at least one meaningful atomic
      for (const sourceProperty of Object.keys(atomicsBySource)) {
        meaningful[sourceProperty] = regularUpdates[sourceProperty];
      }
    }

    // Add variable updates if they differ from current
    for (const [key, value] of Object.entries(variableUpdates)) {
      // Get current value from raw first, then from decomposed
      let currentValue = current.raw[key];
      if (currentValue === undefined && current.decomposed[key]) {
        currentValue = current.decomposed[key][0]?.value;
      }

      // Variables are always meaningful if the value differs or doesn't exist
      if (value !== currentValue) {
        meaningful[key] = value;
      }
    }

    return meaningful;
  }

  /**
   * Decomposes a property to its atomic values with depth tracking.
   * Depth indicates specificity: 0 = atomic property, 1+ = decomposed from shorthand(s).
   *
   * @param propertyType - CSS property to decompose
   * @param value - Property value
   * @param currentDepth - Current decomposition depth (0 for direct properties)
   * @returns Map of atomic properties with values and depths
   */
  private decomposePropertyToAtomicsWithDepth(
    propertyType: string,
    value: string,
    currentDepth: number,
  ): Record<string, { value: string; depth: number }> {
    const atomics: Record<string, { value: string; depth: number }> = {};

    if (!isShorthand(propertyType)) {
      // Already atomic - use current depth
      atomics[propertyType] = { value, depth: currentDepth };
      return atomics;
    }

    // Decompose shorthand recursively
    const decomposed = decompose(propertyType, value);
    if (!decomposed) {
      // Decomposition failed - treat as atomic
      atomics[propertyType] = { value, depth: currentDepth };
      return atomics;
    }

    // Recursively decompose any shorthand values with incremented depth
    const nextDepth = currentDepth + 1;
    for (const [key, atomValue] of Object.entries(decomposed)) {
      if (atomValue !== null) {
        const childAtomics = this.decomposePropertyToAtomicsWithDepth(
          key,
          atomValue,
          nextDepth,
        );
        Object.assign(atomics, childAtomics);
      }
    }

    return atomics;
  }

  /**
   * Normalizes a CSS value by treating 'spx' (scaled pixels) as 'px' (pixels).
   * This ensures semantic equivalence for unit comparison.
   */
  private normalizeValue(value: string): string {
    return value.replace(/spx\b/g, 'px');
  }

  /**
   * Checks if a value contains a CSS variable reference.
   * Returns true for any value containing var().
   */
  private isCssVariable(value: string): boolean {
    if (!value) {
      return false;
    }
    return value.trim().includes('var(');
  }
}
